import { merge } from "lodash-es";
import { combineServices } from "../../../../../helpers";
import {
  type ChildMachineConfig,
  ParentState,
  PassengerInformationChildState,
  PassengerInformationState,
  PassportState,
} from "../../../../../types/common";

import { ReviewGuardTypes } from "../../../../common/Review";
import { FlightServices } from "../../index";
import { ActionTypes } from "./actions";
import { FlightPassengerEventTypesV2 } from "./events";
import { GuardTypes } from "./guards";
import { services, ServiceTypes } from "./services";

export const getMachineStateV2 = (config: ChildMachineConfig) => ({
  id: ParentState.passengerInformation,
  initial: PassengerInformationState.loading,
  states: merge(
    {
      [PassengerInformationState.loading]: {
        initial: PassengerInformationChildState.fetch,
        states: {
          [PassengerInformationChildState.fetch]: {
            invoke: {
              src: ServiceTypes.fetchUserPassengersService,
              onDone: {
                target: `#${config.parentMachineId}.${ParentState.passengerInformation}.${PassengerInformationState.loading}.${PassengerInformationChildState.route}`,
                actions: [
                  ActionTypes.setUserPassengers,
                  ActionTypes.setSingleDefaultAdult,
                  ActionTypes.setEditPassengers,
                ],
              },
              onError: {
                target: `#${config.parentMachineId}.${ParentState.passengerInformation}.${PassengerInformationState.error}`,
                actions: [
                  ActionTypes.setPassengersError,
                  ActionTypes.resetEditPassengers,
                ],
              },
            },
          },
          [PassengerInformationChildState.route]: {
            always: [
              {
                target: `#${config.parentMachineId}.${ParentState.passengerInformation}.${PassengerInformationState.passengerPicker}`,
                cond: GuardTypes.haveAnyPassengersLoaded,
              },
              {
                target: `#${config.parentMachineId}.${ParentState.passengerInformation}.${PassengerInformationState.passengerForm}`,
              },
            ],
          },
        },
      },
      [PassengerInformationState.passengerPicker]: {
        initial: PassengerInformationChildState.idle,
        states: {
          [PassengerInformationChildState.idle]: {},
          [PassengerInformationChildState.delete]: {
            invoke: {
              src: ServiceTypes.deleteUserPassengersService,
              onDone: {
                actions: ActionTypes.handleDeletePassenger,
                target: `#${config.parentMachineId}.${ParentState.passengerInformation}.${PassengerInformationState.passengerPicker}.${PassengerInformationChildState.validate}`,
              },
              onError: {
                actions: ActionTypes.setPassengersError,
                target: `#${config.parentMachineId}.${ParentState.passengerInformation}.${PassengerInformationState.error}`,
              },
            },
          },
          [PassengerInformationChildState.validate]: {
            invoke: {
              src: (ctx, e) =>
                combineServices([
                  services.validateUserPassengersService(ctx, e),
                  FlightServices.fetchTripPricingService(ctx),
                ]),
              onDone: [
                {
                  actions: [
                    ActionTypes.onFetchTripPricing,
                    ActionTypes.setValidated,
                    ActionTypes.clearPassengerInformationError,
                  ],
                  target: `#${config.parentMachineId}.${ParentState.passengerInformation}.${PassengerInformationState.passengerPicker}.${PassengerInformationChildState.idle}`,
                },
              ],
              onError: {
                target: `#${config.parentMachineId}.${ParentState.passengerInformation}.${PassengerInformationState.error}`,
                actions: [
                  ActionTypes.dismissNumPassengerAlert,
                  ActionTypes.onValidateAndTripPricingError,
                ],
              },
            },
          },
        },
        on: {
          [FlightPassengerEventTypesV2.SELECT_PASSENGER]: [
            {
              actions: [
                ActionTypes.resetValidated,
                ActionTypes.selectPassenger,
              ],
              target: `#${config.parentMachineId}.${ParentState.passengerInformation}.${PassengerInformationState.passengerPicker}.${PassengerInformationChildState.validate}`,
              cond: GuardTypes.willHaveSelectedPassengers,
            },
            {
              actions: ActionTypes.selectPassenger,
            },
          ],
          [FlightPassengerEventTypesV2.NEXT]: {
            target: `#${config.parentMachineId}.${ParentState.passengerInformation}.${PassengerInformationState.validate}`,
            cond: GuardTypes.hasAnySelectedPassengers,
          },
        },
      },
      [PassengerInformationState.passengerForm]: {
        initial: PassengerInformationChildState.route,
        states: {
          [PassengerInformationChildState.route]: {
            always: [
              {
                cond: GuardTypes.hasCurrentUserPassenger,
                target: `#${config.parentMachineId}.${ParentState.passengerInformation}.${PassengerInformationState.passengerForm}.${PassengerInformationChildState.edit}`,
              },
              {
                target: `#${config.parentMachineId}.${ParentState.passengerInformation}.${PassengerInformationState.passengerForm}.${PassengerInformationChildState.new}`,
              },
            ],
          },
          [PassengerInformationChildState.edit]: {
            invoke: {
              src: ServiceTypes.trackEditTravelers,
              onDone: {
                target: `#${config.parentMachineId}.${ParentState.passengerInformation}.${PassengerInformationState.passengerForm}.${PassengerInformationChildState.idle}`,
              },
              onError: {
                target: `#${config.parentMachineId}.${ParentState.passengerInformation}.${PassengerInformationState.passengerForm}.${PassengerInformationChildState.idle}`,
              },
            },
          },
          [PassengerInformationChildState.new]: {
            invoke: {
              src: ServiceTypes.trackNewTraveler,
              onDone: {
                target: `#${config.parentMachineId}.${ParentState.passengerInformation}.${PassengerInformationState.passengerForm}.${PassengerInformationChildState.idle}`,
              },
              onError: {
                target: `#${config.parentMachineId}.${ParentState.passengerInformation}.${PassengerInformationState.passengerForm}.${PassengerInformationChildState.idle}`,
              },
            },
          },
          [PassengerInformationChildState.idle]: {},
          [PassengerInformationChildState.update]: {
            invoke: {
              src: ServiceTypes.updateUserPassengerService,
              onDone: {
                target: `#${config.parentMachineId}.${ParentState.passengerInformation}.${PassengerInformationState.passengerPicker}.${PassengerInformationChildState.validate}`,
                actions: [
                  ActionTypes.clearPassengerInformationError,
                  ActionTypes.setUserPassengers,
                ],
              },
              onError: {
                target: `#${config.parentMachineId}.${ParentState.passengerInformation}.${PassengerInformationState.error}`,
                actions: ActionTypes.setPassengersError,
              },
            },
          },
        },
        on: {
          [FlightPassengerEventTypesV2.UPDATE_PASSENGER]: {
            target: `#${config.parentMachineId}.${ParentState.passengerInformation}.${PassengerInformationState.passengerForm}.${PassengerInformationChildState.update}`,
          },
        },
      },
      [PassengerInformationState.validate]: {
        invoke: {
          src: (ctx, e) =>
            combineServices([
              services.validateUserPassengersService(ctx, e),
              FlightServices.fetchTripPricingService(ctx),
            ]),
          onDone: [
            {
              cond: ReviewGuardTypes.visitedReview,
              target: config.nextStateFromReview || ParentState.cartQuote,
              actions: [
                ActionTypes.onFetchTripPricing,
                ActionTypes.setValidated,
                ActionTypes.resetEditPassengers,
              ],
            },
            {
              target: config.nextState || ParentState.passengerInformation,
              actions: [
                ActionTypes.onFetchTripPricing,
                ActionTypes.setValidated,
                ActionTypes.resetEditPassengers,
              ],
            },
          ],
          onError: {
            target: `#${config.parentMachineId}.${ParentState.passengerInformation}.${PassengerInformationState.error}`,
            actions: [
              ActionTypes.dismissNumPassengerAlert,
              ActionTypes.onValidateAndTripPricingError,
              ActionTypes.resetValidated,
            ],
          },
        },
      },
      [PassengerInformationState.error]: {},
    },
    config.stateOverride
  ),
  on: {
    [FlightPassengerEventTypesV2.DELETE_PASSENGER]: {
      target: `#${config.parentMachineId}.${ParentState.passengerInformation}.${PassengerInformationState.passengerPicker}.${PassengerInformationChildState.delete}`,
    },
    [FlightPassengerEventTypesV2.SET_CURRENT_PASSENGER]: {
      actions: ActionTypes.setCurrentPassenger,
    },
    [FlightPassengerEventTypesV2.CLEAR_PASSENGER_INFORMATION_ERROR]: {
      actions: ActionTypes.clearPassengerInformationError,
      target: `#${config.parentMachineId}.${ParentState.passengerInformation}.${PassengerInformationState.passengerPicker}`,
    },
    [FlightPassengerEventTypesV2.OPEN_PASSENGER_PICKER]: `#${config.parentMachineId}.${ParentState.passengerInformation}.${PassengerInformationState.passengerPicker}`,

    [FlightPassengerEventTypesV2.OPEN_PASSENGER_FORM]: `#${config.parentMachineId}.${ParentState.passengerInformation}.${PassengerInformationState.passengerForm}`,
    [FlightPassengerEventTypesV2.CLEAR_CURRENT_INFANT_ID]: {
      actions: ActionTypes.clearCurrentInfantId,
    },
    [FlightPassengerEventTypesV2.ON_INFANT_MODAL_CONTINUE]: {
      actions: ActionTypes.onInfantModalContinue,
      target: `#${config.parentMachineId}.${ParentState.passengerInformation}.${PassengerInformationState.passengerPicker}.${PassengerInformationChildState.validate}`,
    },
    [FlightPassengerEventTypesV2.OPEN_FORM_AND_SET_PASSENGER]: {
      actions: ActionTypes.setCurrentPassenger,
      target: `#${config.parentMachineId}.${ParentState.passengerInformation}.${PassengerInformationState.passengerForm}`,
    },
    [FlightPassengerEventTypesV2.OPEN_PASSPORT_FORM]: `#${config.parentMachineId}.${ParentState.passport}.${PassportState.idle}`,

    [FlightPassengerEventTypesV2.PREVIOUS]: {
      target: config?.previousState || ParentState.contactInformation,
    },
    [FlightPassengerEventTypesV2.COMPLETE]: [
      {
        cond: GuardTypes.notValidated,
        target: `#${config.parentMachineId}.${ParentState.passengerInformation}.${PassengerInformationState.validate}`,
      },
      {
        cond: ReviewGuardTypes.visitedReview,
        target: config.nextStateFromReview || ParentState.cartQuote,
      },
      {
        target: config.nextState || ParentState.passengerInformation,
      },
    ],
    [FlightPassengerEventTypesV2.CLEAR_LAST_SELECTED_PASSENGER_ID]: {
      actions: ActionTypes.clearLastSelectedPassengerId,
    },
  },
});
